import React, { useEffect } from 'react';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { NamedLink, NotificationBadge } from '../../components';
import { useDashboardMode } from '../../context/DashboardModeContext';
import { fetchUnreadMessages } from '../../ducks/user.duck';
import * as log from '../../util/log';
import css from './DashboardSidebar.module.css';

// Mode-aware dashboard nav. DashboardModeContext tells us which mode the
// user is in; canSwitchModes is false for pure renters (no listings), in
// which case we always render the renting sidebar regardless of mode.
//
// Items that share a route in both modes (InboxPage, etc.) reuse the
// same `page` identifier for active-state highlighting.

// `modeOnClick` field: when a nav item is mode-specific (Bookings only
// makes sense in hosting, My Rentals only makes sense in renting, etc.),
// clicking it also flips the DashboardModeContext to the matching mode.
// This replaces the previous auto-switch-on-URL behavior without needing
// a global effect hook. Shared items (Messages, Settings) leave the mode
// alone. Overview uses mode-scoped routes so the two dashboards can live
// at separate URLs instead of a shared /account/dashboard wrapper.

// ─── Renting mode ─────────────────────────────────────────────────────
// "My Rentals" removed — previously routed to the same URL as Overview
// (/account/rentals), so was a dead-click from the Overview page.
// Overview is the renter's rentals hub. A dedicated full-list page can
// be added later if product needs one — built as a real feature, not
// a sidebar alias.
const RENTING_NAV = [
  { emoji: '📊', label: 'Dashboard',  page: 'RenterDashboardPage', linkName: 'RenterDashboardPage', modeOnClick: 'renting' },
  { emoji: '💬', label: 'Inbox',      page: 'InboxPage',           linkName: 'InboxPage' },
  { emoji: '❤️', label: 'Favorites',  page: 'FavoritesPage',       linkName: 'FavoritesPage',       modeOnClick: 'renting' },
  { emoji: '⚙️', label: 'Settings',   page: 'AccountSettingsPage', linkName: 'ProfileSettingsPage' },
];

// ─── Hosting mode ─────────────────────────────────────────────────────
// Earnings section intentionally absent. EarningsPage was deleted
// entirely (route + component + duck) rather than showing a
// "coming soon" placeholder — a real Earnings feature ships later
// as its own feature commit.
const HOSTING_NAV = [
  { emoji: '📊', label: 'Dashboard',   page: 'HostDashboardPage',   linkName: 'HostDashboardPage', modeOnClick: 'hosting' },
  { emoji: '📬', label: 'Bookings',    page: 'BookingsPage',        linkName: 'BookingsPage',      modeOnClick: 'hosting' },
  { emoji: '📦', label: 'My Listings', page: 'ManageListingsPage',  linkName: 'ManageListingsPage', modeOnClick: 'hosting' },
  // Calendar lands on CalendarPickerPage. With one listing it auto-
  // redirects to that listing's availability tab; with multiple it
  // shows a picker. See CalendarPickerPage.js.
  { emoji: '📅', label: 'Calendar',    page: 'CalendarPickerPage',  linkName: 'CalendarPickerPage', modeOnClick: 'hosting' },
  { emoji: '📚', label: 'Resources',   page: 'HostResourcesPage',   linkName: 'HostResourcesPage',  modeOnClick: 'hosting' },
  { emoji: '💬', label: 'Inbox',       page: 'InboxPage',           linkName: 'InboxPage' },
  { emoji: '⚙️', label: 'Settings',    page: 'AccountSettingsPage', linkName: 'ProfileSettingsPage' },
];

const DashboardSidebar = ({ currentPage, role }) => {
  const dispatch = useDispatch();
  const { currentMode, canSwitchModes, setMode } = useDashboardMode();
  const unreadMessageTxIds = useSelector(state => state.user?.unreadMessageTxIds || []);

  // Keep the Messages badge fresh while the user is active in the app.
  // Without this, `fetchUnreadMessages` only fires on app-load / login
  // (via fetchCurrentUser), so a new message arriving mid-session
  // doesn't bump the badge until the user hard-reloads — defeats the
  // "at-a-glance awareness" purpose of the badge.
  //
  // Two trigger points:
  //   1. On every dashboard-page mount (the sidebar is only rendered
  //      inside DashboardLayout, so each navigation to an account /
  //      dashboard / bookings / inbox / etc. page re-fires this).
  //   2. On window focus (user switches back to the tab from email,
  //      Slack, another browser tab — same pattern as Gmail/Slack
  //      refreshing their badges).
  // Both fire cheap no-ops when there's nothing new; the thunk
  // returns an array and the reducer early-exits if it matches.
  useEffect(() => {
    // Non-critical — a failed unread scan just means the badge won't
    // update this tick. No user-facing error, but it must reach Sentry.
    dispatch(fetchUnreadMessages()).catch(e => {
      log.error(e, 'fetch-unread-messages-failed');
    });
    const onFocus = () => {
      dispatch(fetchUnreadMessages()).catch(e => {
        log.error(e, 'fetch-unread-messages-failed');
      });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [dispatch]);

  // Nav follows currentMode directly. We previously also gated on
  // canSwitchModes (hasListings), but that created a window on first
  // paint — before the currentUser API call completes — where the
  // sidebar rendered RENTING_NAV even for a dual-role user whose
  // stored preference was 'hosting'. That made the Overview link
  // point to /account/rentals instead of /account/host-dashboard.
  // Pure renters are already safe: DashboardModeContext's
  // computeDesiredMode corrector forces stored='hosting' + !hasListings
  // back to 'renting', so currentMode never becomes 'hosting' for them.
  const inHostingMode = currentMode === 'hosting';
  const navItems = inHostingMode ? HOSTING_NAV : RENTING_NAV;

  // Messages badge = number of transaction threads with an unread message
  // from the other party. Cleared per-thread when the user opens it.
  const totalMessageCount = unreadMessageTxIds.length;

  // Inbox tab default follows mode: hosting → sales, renting → orders.
  // A `role` prop still wins when the caller explicitly passes it.
  const defaultInboxTab =
    role === 'provider'
      ? 'sales'
      : role === 'customer'
      ? 'orders'
      : inHostingMode
      ? 'sales'
      : 'orders';

  const isMessagesItem = item =>
    item.linkName === 'InboxPage' || item.page === 'InboxPage' || item.page === 'InboxPageMessages';

  return (
    <div className={css.sidebar}>
      {navItems.map(item => {
        const isActive = currentPage === item.page;
        const isMessages = isMessagesItem(item);

        const linkParams = isMessages ? { tab: defaultInboxTab } : undefined;

        // Flip mode on click for mode-specific items. setMode is a
        // no-op if the mode already matches. Runs in the same React
        // batch as the Link's navigate, so the next render sees both
        // the new URL and the new mode together.
        const handleClick = item.modeOnClick
          ? () => setMode(item.modeOnClick)
          : undefined;

        return (
          <NamedLink
            key={`${item.label}-${item.page}`}
            name={item.linkName}
            params={linkParams}
            onClick={handleClick}
            className={classNames(css.navItem, { [css.navItemActive]: isActive })}
          >
            <span className={css.navEmoji}>{item.emoji}</span>
            <span className={css.navLabel}>{item.label}</span>
            {isMessages && totalMessageCount > 0 ? (
              <NotificationBadge className={css.navBadge} count={totalMessageCount} />
            ) : null}
          </NamedLink>
        );
      })}
    </div>
  );
};

export default DashboardSidebar;
